import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingEmbedRef, LevelingStepDefinition } from '@/types/leveling'
import { Button, Modal, Tabs, insertIf, useStore } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import { LevelingTitleBox } from 'leveling/widgets/leveling-title-box'
import { TipsAndTricks } from 'leveling/widgets/tips-and-tricks'
import React from 'react'
import Store from 'store'
import Slider from 'ui/slider'
import { WithStore, useEmbed } from '../../common'
import { Widget } from '../../widget'
import { PassiveTree } from '../../widgets/passive-tree'
import './index.scss'
import { LevelingAscendancy } from './leveling-ascendancy'
import { LevelingGemAquisition } from './leveling-gem-aquisition'
import { LevelingSkills } from './leveling-skills'

export function getByLevel(
  steps: LevelingStepDefinition[],
  attr: Exclude<keyof LevelingStepDefinition, 'level'>,
  level: number,
  takeFirstAsDefault = true
) {
  let result:
    | {
        level: number
        ref: LevelingEmbedRef
      }
    | undefined

  const orderedSteps = steps.slice().sort((a, b) => a.level - b.level)

  for (const step of orderedSteps) {
    const ref = step[attr]
    if (ref?.id === undefined) continue

    if (takeFirstAsDefault && !result) {
      result = {
        level: step.level,
        ref
      }
    }

    if (step.level > (result?.level ?? 0) && step.level <= level)
      result = {
        level: step.level,
        ref
      }
  }

  return result
}

const minLevel = 1
const maxLevel = 70

export function LevelingTab({
  embed,
  onEmbedChange,
  stores,
  levelOverride,
  onLevelChange,
  metadataId,
  isActiveTab,
  onLevelingCompleted
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
  stores: Record<string, Store>
  levelOverride?: number
  onLevelChange?: (level: number) => void
  metadataId: string
  isActiveTab?: boolean
  onLevelingCompleted: () => void
}) {
  const [viewport] = useStore((store) => ({
    md: store.viewport.md,
    xl: store.viewport.xl
  }))
  const [level, setLevel] = React.useState(1)

  const updateLevel = onLevelChange ?? setLevel

  const onPrevPoint = React.useCallback(() => updateLevel(Math.max(minLevel, level - 1)), [level, updateLevel])
  const onNextPoint = React.useCallback(() => updateLevel(Math.min(maxLevel, level + 1)), [level, updateLevel])
  const levelRef = React.useRef(level)
  levelRef.current = level
  React.useEffect(() => {
    if (levelOverride !== undefined && levelOverride !== levelRef.current) {
      setLevel(levelOverride)
    }
  }, [levelOverride])

  // Level-dependent
  const skillsStep = React.useMemo(() => getByLevel(embed.steps, 'skills', level), [embed.steps, level])
  const activeSkills = useEmbed(stores, skillsStep?.ref, 'skills', 'skills-video-display')

  const treeStep = React.useMemo(() => getByLevel(embed.steps, 'passives', level), [embed.steps, level])
  const activeTree = useEmbed(stores, treeStep?.ref, 'passives')

  const [tipsAndTricksCollapsed, setTipsAndTricksCollapsed] = React.useState(false)

  const [showProgressionTipModal, setShowProgressionTipModal] = React.useState(false)
  const progressionTipDisplayed = React.useRef(false)
  React.useEffect(() => {
    if (progressionTipDisplayed.current === true) return
    if (level === maxLevel) {
      setShowProgressionTipModal(true)
      // Uncomment to only show the progression tip once
      // progressionTipDisplayed.current = true
    }
  }, [level])

  return (
    <Widget title="Leveling" className="poe-leveling-tab">
      <div className="left">
        <div className="poe-progression-content">
          <div className="left">
            <LevelingTitleBox type="sm" embed={embed} onEmbedChange={onEmbedChange} stores={stores} />

            <div className="poe-level-progression">
              <span className="poe-level">Level: {level}</span>

              <div className="poe-progression">
                <span
                  className={classNames('poe-button poe-minus', {
                    'poe-disabled': level <= minLevel
                  })}
                  onClick={onPrevPoint}
                />
                <Slider
                  min={minLevel}
                  max={maxLevel}
                  step={1}
                  value={level}
                  onChange={updateLevel}
                  className="ui-Slider"
                />
                <span
                  className={classNames('poe-button poe-plus', {
                    'poe-disabled': level >= maxLevel - 1
                  })}
                  onClick={onNextPoint}
                />
              </div>
            </div>

            {viewport.md ? (
              <>
                <LevelingSkills
                  embed={embed}
                  onEmbedChange={onEmbedChange}
                  isActiveTab={isActiveTab}
                  skillsStep={skillsStep}
                  activeSkills={activeSkills}
                />
                <LevelingAscendancy embed={embed} stores={stores} level={level} />
                <LevelingGemAquisition embed={embed} stores={stores} activeSkills={activeSkills} />
              </>
            ) : (
              <Tabs
                headersClassName="poe-leveling-grouped-header"
                headerSpacing={{ type: 'justify-between' }}
                scrollOnTabChange={{
                  type: 'left',
                  leftOffset: 20,
                  topOffset: 132
                }}
                tabs={[
                  {
                    selector: 'Gems',
                    content: (
                      <div className="poe-leveling-grouped">
                        <LevelingSkills
                          embed={embed}
                          onEmbedChange={onEmbedChange}
                          isActiveTab={isActiveTab}
                          skillsStep={skillsStep}
                          activeSkills={activeSkills}
                        />

                        <LevelingAscendancy embed={embed} stores={stores} level={level} />

                        <LevelingGemAquisition embed={embed} stores={stores} activeSkills={activeSkills} />
                      </div>
                    )
                  },
                  {
                    selector: 'Passives',
                    content:
                      activeTree && isActiveTab ? (
                        <WithStore store={activeTree.store}>
                          <PassiveTree
                            embed={activeTree.embed}
                            active={activeTree.active}
                            level={level}
                            renderBackground={false}
                            startFromActivePos={true}
                            bandits={embed.overview.bandits}
                          />
                        </WithStore>
                      ) : null
                  },
                  ...insertIf(embed.showTipsAndTricks == true, {
                    selector: 'Tips & Tricks',
                    content: (
                      <div className="poe-leveling-grouped">
                        <TipsAndTricks embed={embed} onEmbedChange={onEmbedChange} isActiveTab={isActiveTab} />
                      </div>
                    )
                  })
                ]}></Tabs>
            )}
          </div>

          {viewport.md && (
            <div className="right">
              {activeTree && isActiveTab && (
                <WithStore store={activeTree.store}>
                  <PassiveTree
                    embed={activeTree.embed}
                    active={activeTree.active}
                    level={level}
                    renderBackground={false}
                    startFromActivePos={true}
                    bandits={embed.overview.bandits}
                  />
                </WithStore>
              )}
            </div>
          )}
        </div>
      </div>

      {viewport.md && embed.showTipsAndTricks && (
        <div className={classNames('right', tipsAndTricksCollapsed && 'poe-collapsed')}>
          <TipsAndTricks embed={embed} onEmbedChange={onEmbedChange} isActiveTab={isActiveTab} />
          <span
            className="poe-arrow"
            onClick={() => {
              setTipsAndTricksCollapsed(!tipsAndTricksCollapsed)
            }}>
            <span></span>
          </span>
        </div>
      )}

      <Modal
        className="poe-notification-modal"
        innerClassName="poe-notification-modal-content"
        visible={showProgressionTipModal}
        onClose={() => {
          setShowProgressionTipModal(false)
        }}>
        <h3>Leveling Completed!</h3>
        You have finished the leveling section of this guide. <br />
        Jump to the progression tab in order to continue upgrading your character by following the milestones.
        <div className="poe-action-buttons">
          <Button
            onClick={() => {
              setShowProgressionTipModal(false)
            }}>
            Close
          </Button>
          <Button
            onClick={() => {
              setShowProgressionTipModal(false)
              onLevelingCompleted()
            }}>
            Next
          </Button>
        </div>
      </Modal>
    </Widget>
  )
}
